<template>
  <section
    class="what-i-do"
    ref="sectionRef"
    id="whatido"
    @mouseenter="handleSectionMouseEnter"
    @mousemove="handleSectionMouseMove"
    @mouseleave="handleSectionMouseLeave"
    @click="handleSectionClick"
  >
    <div class="what-i-do__inner">
      <canvas ref="kiCanvas" class="ki-canvas"></canvas>

      <div
        class="bioma-insects-overlay"
        :class="{ 'bioma-insects-overlay--visible': isCreativeHovered }"
      >
        <BiomaInsects ref="biomaRef" :visible="isCreativeHovered" />
      </div>

      <div class="what-i-do__container">
        <div class="what-i-do__header" ref="headerRef">
          <h2 class="what-i-do__title what-i-do__title-accent">
            {{ $t('page.whatIDo.title.first') }} {{ $t('page.whatIDo.title.highlight') }}
          </h2>
        </div>

        <div
          class="what-i-do__services"
          :class="{ 'humanity-active': isHumanityActive, 'has-interacted': hasInteracted }"
          ref="servicesRef"
        >
          <div
            v-for="(service, index) in services"
            :key="index"
            class="service-item"
            :class="{
              'service-item--has-frame': service.hasFrame,
              'service-item--active': isMobileModalOpen && currentServiceIndex === index,
              'is-hovered': hoveredServices.has(index),
              'service-item--dimmed':
                (hoveredServices.size > 0 && !hoveredServices.has(index)) ||
                (isMobileModalOpen &&
                  currentServiceIndex !== null &&
                  currentServiceIndex !== index),
              'service-item--humanity': service.hasLightEffect,
            }"
            :ref="(el) => (serviceItemRefs[index] = el)"
            @mouseenter="handleMouseEnter(index, $event)"
            @mouseleave="handleMouseLeave(index)"
            @mousemove="handleServiceMouseMove($event, index)"
            @click="handleServiceClick(index)"
            :style="service.hasLightEffect ? humanityLightStyle : {}"
          >
            <!-- FRONTEND: code tags -->
            <template v-if="service.hasTags">
              <span class="tag tag--open">&lt;</span>
              <span class="tag tag--close">/&gt;</span>
            </template>

            <!-- UI/UX: Figma frame -->
            <div v-if="service.hasFrame" class="service-item__frame">
              <span class="service-item__frame-corner service-item__frame-corner--tl"></span>
              <span class="service-item__frame-corner service-item__frame-corner--tr"></span>
              <span class="service-item__frame-corner service-item__frame-corner--bl"></span>
              <span class="service-item__frame-corner service-item__frame-corner--br"></span>
              <span class="service-item__frame-size">70 × 7</span>
            </div>

            <!-- INTERACTION: ripple rings on entry -->
            <div v-if="service.hasInteractionEffect" class="interaction-effect">
              <div class="interaction-ripples"></div>
            </div>

            <!-- Title -->
            <h3 class="service-item__title" :class="{ 'creative-glow': service.hasCreativeEffect }">
              <span class="service-item__text service-item__text--bg" aria-hidden="true">{{
                service.title
              }}</span>

              <!-- HUMANITY: text-based outer glow -->
              <span
                v-if="service.hasLightEffect"
                class="service-item__text service-item__text--glow"
                aria-hidden="true"
              >
                {{ service.title }}
              </span>

              <span
                class="service-item__text service-item__text--fill"
                :style="{ clipPath: `inset(0 ${100 - service.progress}% 0 0)` }"
                >{{ service.title }}</span
              >

              <!-- HUMANITY: dynamic bright border/stroke -->
              <span class="service-item__text service-item__text--stroke" aria-hidden="true">
                {{ service.title }}
              </span>
            </h3>
          </div>
        </div>
      </div>

      <!-- Tooltip -->
      <div
        class="service-tooltip"
        :class="{ 'service-tooltip--visible': isTooltipVisible }"
        :style="tooltipStyle"
        ref="tooltipRef"
      >
        <span class="service-tooltip__icon">✦</span>
        <div class="service-tooltip__content">
          <div class="service-tooltip__text-wrapper" ref="tooltipTextWrapperRef">
            <span class="service-tooltip__text" ref="tooltipTextRef">{{ currentTooltipText }}</span>
          </div>
        </div>
      </div>

      <!-- Custom cursor -->
      <div
        class="custom-cursor"
        ref="cursorRef"
        :class="{
          'custom-cursor--visible': isCursorVisible && cursorState !== 'hidden',
          'custom-cursor--pointer': cursorState === 'pointer',
        }"
      >
        <img
          src="@/assets/images/cursor-pointer.svg"
          alt=""
          class="custom-cursor__icon"
          :style="{ transform: `rotate(${cursorRotation}deg)` }"
        />
      </div>

      <!-- Lantern cursor — held by the mouse, fireflies follow its light -->
      <div
        v-if="isDarkMode"
        class="lantern-grip"
        :class="{ 'lantern-grip--visible': isCreativeHovered }"
        :style="lanternGripStyle"
      >
        <div class="lantern-body" :style="lanternBodyStyle">
          <div class="lantern-glow" ref="lanternGlowRef"></div>
          <img src="@/assets/images/lantern.png" alt="" class="lantern-img" draggable="false" />
        </div>
      </div>

      <!-- Flower cursor — held by the mouse in light mode, butterflies are drawn to it -->
      <div
        v-if="!isDarkMode"
        class="flower-grip"
        :class="{ 'flower-grip--visible': isCreativeHovered }"
        :style="flowerGripStyle"
      >
        <div class="flower-body" :style="flowerBodyStyle">
          <div class="flower-glow" ref="flowerGlowRef"></div>
          <svg
            class="flower-sprite"
            width="24"
            height="56"
            viewBox="0 0 6 14"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="2" y="0" width="2" height="1" fill="#F5F5F5" />
            <rect x="1" y="1" width="4" height="1" fill="#F5F5F5" />
            <rect x="0" y="2" width="6" height="1" fill="#F5F5F5" />
            <rect x="0" y="3" width="2" height="1" fill="#F5F5F5" />
            <rect x="4" y="3" width="2" height="1" fill="#F5F5F5" />
            <rect x="2" y="3" width="2" height="2" fill="#7C3AED" />
            <rect x="0" y="4" width="2" height="1" fill="#F5F5F5" />
            <rect x="4" y="4" width="2" height="1" fill="#F5F5F5" />
            <rect x="0" y="5" width="6" height="1" fill="#F5F5F5" />
            <rect x="1" y="6" width="4" height="1" fill="#F5F5F5" />
            <rect x="2" y="7" width="2" height="1" fill="#F5F5F5" />
            <rect x="2" y="8" width="2" height="6" fill="#059669" />
            <rect x="1" y="10" width="1" height="2" fill="#10B981" />
          </svg>
        </div>
      </div>

      <TiltIndicator :show="showTiltIndicator" />

      <!-- Mobile tap hint — icon taps directly on the first service -->
      <div
        class="tap-hint"
        :class="{ 'tap-hint--visible': !isDesktop && !hasInteracted }"
        :style="tapHintStyle"
      >
        <div class="tap-hint__icon">
          <Tap02Icon class="tap-hint__svg" />
          <span class="tap-hint__ripple"></span>
        </div>
        <span class="tap-hint__label">{{ $t('common.tapToExplore') }}</span>
      </div>

      <!-- Mobile service modal -->
      <Teleport to="body">
        <Transition name="modal-slide">
          <div
            v-if="!isDesktop && isMobileModalOpen && currentServiceIndex !== null"
            class="mobile-service-modal-backdrop"
            @click="closeMobileModal"
          >
            <div class="mobile-service-modal" @click.stop>
              <div class="mobile-service-modal__header">
                <h3 class="mobile-service-modal__title">
                  {{ services[currentServiceIndex].title }}
                </h3>
                <button class="mobile-service-modal__close" @click="closeMobileModal">
                  <Cancel01Icon size="24" style="color: var(--color-text-muted)" />
                </button>
              </div>
              <div class="mobile-service-modal__body">
                <p class="mobile-service-modal__description">
                  {{ services[currentServiceIndex].description }}
                </p>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
  </section>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useI18n } from 'vue-i18n'
import BiomaInsects from './BiomaInsects.vue'
import TiltIndicator from '@/components/common/TiltIndicator.vue'
import { useDevice } from '@/composables/useDevice'
import { Tap02Icon, Cancel01Icon } from 'hugeicons-vue'

gsap.registerPlugin(ScrollTrigger)

const { t } = useI18n()

const SCROLL_DEACTIVATION_THRESHOLD = 200
const MOBILE_SCROLL_OFFSET_FACTOR = 0.45

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Refs
const sectionRef = ref(null)
const servicesRef = ref(null)
const headerRef = ref(null)
const serviceItemRefs = ref([])
const biomaRef = ref(null)
const tooltipRef = ref(null)
const tooltipTextRef = ref(null)
const tooltipTextWrapperRef = ref(null)
const cursorRef = ref(null)
const kiCanvas = ref(null)

// State
const hoveredServices = ref(new Set())
const currentServiceIndex = ref(null)
const tooltipX = ref(0)
const tooltipY = ref(0)
const hasInteracted = ref(false)
const isMobileModalOpen = ref(false)
const isCursorVisible = ref(false)
const cursorX = ref(0)
const cursorY = ref(0)
const cursorState = ref('default')
const cursorRotation = ref(0)
const humanityLightX = ref(0)
const humanityLightY = ref(0)
const showTiltIndicator = ref(false)
const isAutoScrolling = ref(false)
const { isDesktop } = useDevice()

// ─── Lantern pendulum state ────────────────────────────────────────────────
const lanternGlowRef = ref(null)
const isDarkMode = ref(document.documentElement.classList.contains('dark'))
const pendulumAngle = ref(0)
let pendulumOmega = 0
let smoothMouseVelX = 0 // low-pass filtered velocity → eliminates jerk spikes
let prevMouseXForPendulum = 0
let prevMouseTimeForPendulum = 0
let pendulumRafId = null
let themeObserver = null

// ─── Global mouse position (needed to init cursor when section scrolls into view)
let lastMouseX = 0
let lastMouseY = 0
const trackGlobalMouse = (e) => {
  lastMouseX = e.clientX
  lastMouseY = e.clientY
}

// ─── Flower cursor state (light mode) ──────────────────────────────────────
const flowerGlowRef = ref(null)
let bloomActive = false

let autoScrollTimer = null
let scrollTriggerInstance = null

// Services
const services = reactive([
  {
    title: computed(() => t('page.whatIDo.services.humanity.title')),
    progress: 0,
    description: computed(() => t('page.whatIDo.services.humanity.description')),
    hasLightEffect: true,
  },
  {
    title: computed(() => t('page.whatIDo.services.creative.title')),
    progress: 0,
    description: computed(() => t('page.whatIDo.services.creative.description')),
    hasCreativeEffect: true,
  },
  {
    title: computed(() => t('page.whatIDo.services.uiUx.title')),
    progress: 0,
    description: computed(() => t('page.whatIDo.services.uiUx.description')),
    hasFrame: true,
  },
  {
    title: computed(() => t('page.whatIDo.services.frontend.title')),
    progress: 0,
    description: computed(() => t('page.whatIDo.services.frontend.description')),
    hasTags: true,
  },
  {
    title: computed(() => t('page.whatIDo.services.interaction.title')),
    progress: 0,
    description: computed(() => t('page.whatIDo.services.interaction.description')),
    hasInteractionEffect: true,
  },
])

// Computed
const isTooltipVisible = computed(() => hoveredServices.value.size > 0)

const currentTooltipText = computed(() => {
  if (currentServiceIndex.value !== null && services[currentServiceIndex.value]) {
    return services[currentServiceIndex.value].description
  }
  return ''
})

const isCreativeHovered = computed(() => {
  const creativeIndex = services.findIndex((s) => s.hasCreativeEffect)
  if (creativeIndex === -1) return false
  if (isDesktop.value) {
    return hoveredServices.value.has(creativeIndex)
  }
  return isMobileModalOpen.value && currentServiceIndex.value === creativeIndex
})

const isHumanityActive = computed(() => {
  const humanityIndex = services.findIndex((s) => s.hasLightEffect)
  if (humanityIndex === -1) return false
  if (isDesktop.value) {
    return hoveredServices.value.has(humanityIndex)
  }
  return isMobileModalOpen.value && currentServiceIndex.value === humanityIndex
})

// ─── Genkidama Particles System ────────────────────────────────────────────────
let kiAnimationId = null
let particles = []

watch(isHumanityActive, (active) => {
  if (active) {
    startKiLoop()
  } else {
    stopKiLoop()
  }
})

function startKiLoop() {
  if (!kiCanvas.value || !sectionRef.value) return
  const ctx = kiCanvas.value.getContext('2d')

  const resizeCanvas = () => {
    kiCanvas.value.width = sectionRef.value.offsetWidth
    kiCanvas.value.height = sectionRef.value.offsetHeight
  }
  resizeCanvas()

  function loop() {
    ctx.clearRect(0, 0, kiCanvas.value.width, kiCanvas.value.height)

    // Spawn new particles (cut in half again, very sparse)
    if (Math.random() > 0.55) spawnParticle() // 45% chance

    // Update and draw
    for (let i = particles.length - 1; i >= 0; i--) {
      let p = particles[i]
      const dx = p.targetX - p.x
      const dy = p.targetY - p.y
      const dist = Math.hypot(dx, dy)

      // If particle reached target, remove it
      if (dist < 15) {
        particles.splice(i, 1)
        continue
      }

      // Accelerate towards humanity (slower)
      p.speed += p.acceleration
      p.x += (dx / dist) * p.speed
      p.y += (dy / dist) * p.speed

      // Theme-aware colors
      const isDark = document.documentElement.classList.contains('dark')
      const particleColor = isDark
        ? `rgba(255, 255, 255, ${p.opacity})`
        : `rgba(109, 40, 217, ${p.opacity})` // purple-700 for light mode
      const glowColor = isDark ? 'rgba(196, 181, 253, 0.8)' : 'rgba(139, 92, 246, 0.9)' // violet-500 for light mode
      const trailColor = isDark
        ? `rgba(196, 181, 253, ${p.opacity * 0.5})`
        : `rgba(167, 139, 250, ${p.opacity * 0.7})` // violet-400 for light mode

      // Draw particle glow
      ctx.beginPath()
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2)
      ctx.fillStyle = particleColor
      ctx.shadowBlur = 10
      ctx.shadowColor = glowColor
      ctx.fill()

      // Draw motion trail (lengthened to compensate for lower speed)
      ctx.beginPath()
      ctx.moveTo(p.x, p.y)
      ctx.lineTo(p.x - (dx / dist) * p.speed * 2.5, p.y - (dy / dist) * p.speed * 2.5)
      ctx.strokeStyle = trailColor
      ctx.lineWidth = p.radius
      ctx.stroke()
    }

    kiAnimationId = requestAnimationFrame(loop)
  }

  loop()
}

function stopKiLoop() {
  if (kiAnimationId) cancelAnimationFrame(kiAnimationId)
  particles = []
  if (kiCanvas.value) {
    const ctx = kiCanvas.value.getContext('2d')
    ctx.clearRect(0, 0, kiCanvas.value.width, kiCanvas.value.height)
  }
}

function spawnParticle() {
  const humanityItem = services.findIndex((s) => s.hasLightEffect)
  if (humanityItem === -1) return

  const targetEl = serviceItemRefs.value[humanityItem]
  if (!targetEl) return

  const sectionRect = sectionRef.value.getBoundingClientRect()
  const targetRect = targetEl.getBoundingClientRect()

  // Filter other services as donors, keep their original indices to assign specific segments
  const donors = serviceItemRefs.value
    .map((el, idx) => ({ el, idx }))
    .filter((item) => item.idx !== humanityItem && item.el != null)

  if (!donors.length) return

  const donorObj = donors[Math.floor(Math.random() * donors.length)]
  const donorEl = donorObj.el
  const donorIdx = donorObj.idx

  const donorRect = donorEl.getBoundingClientRect()

  // Start anywhere inside the donor's rect
  const startX = donorRect.left - sectionRect.left + Math.random() * donorRect.width
  const startY = donorRect.top - sectionRect.top + Math.random() * donorRect.height

  // For perfect harmony and zero crossing paths ("sin arroz con mango"):
  // Instead of crossing each other to reach hardcoded segments, particles
  // simply travel straight up from their DOM position towards Humanity.
  const humanityCenter = targetRect.left - sectionRect.left + targetRect.width / 2

  // 85% straight up, 15% pull towards center (creates a beautiful, clean funnel)
  let targetX = startX * 0.85 + humanityCenter * 0.15

  // Clamp to Humanity text bounds so they don't float into empty space
  const hLeft = targetRect.left - sectionRect.left
  const hRight = targetRect.right - sectionRect.left
  targetX = Math.max(hLeft + 20, Math.min(hRight - 20, targetX))

  // Add a tiny random wobble
  targetX += Math.random() * 10 - 5
  // Move the collision point slightly higher up into the body of the text
  const targetY = targetRect.bottom - sectionRect.top - 25

  particles.push({
    x: startX,
    y: startY,
    targetX,
    targetY,
    speed: 0.05 + Math.random() * 0.3,
    acceleration: 0.01 + Math.random() * 0.04,
    radius: 1 + Math.random() * 2,
    opacity: 0.4 + Math.random() * 0.6,
  })
}

const tooltipStyle = computed(() => ({
  left: `${tooltipX.value}px`,
  top: `${tooltipY.value}px`,
}))

const humanityLightStyle = computed(() => ({
  '--light-x': `${humanityLightX.value}px`,
  '--light-y': `${humanityLightY.value}px`,
}))

const tapHintStyle = computed(() => {
  if (isDesktop.value || hasInteracted.value) return { display: 'none' }
  const el = serviceItemRefs.value[0]
  if (!el || !sectionRef.value) return { display: 'none' }
  const svcRect = el.getBoundingClientRect()
  const sectionRect = sectionRef.value.getBoundingClientRect()
  return {
    left: `${svcRect.left - sectionRect.left + svcRect.width / 2}px`,
    top: `${svcRect.top - sectionRect.top - 14}px`,
  }
})

// The "grip" positions the pivot at the exact cursor location.
// The "body" rotates around that pivot — this is the pendulum swing.
const lanternGripStyle = computed(() => ({
  left: `${cursorX.value}px`,
  top: `${cursorY.value}px`,
}))

const lanternBodyStyle = computed(() => ({
  transform: `rotate(${pendulumAngle.value}rad)`,
}))

const flowerGripStyle = computed(() => ({
  left: `${cursorX.value}px`,
  top: `${cursorY.value}px`,
}))

const flowerBodyStyle = computed(() => ({
  transform: `rotate(${pendulumAngle.value}rad)`,
}))

// ─── Lantern pendulum physics ──────────────────────────────────────────────────
// α = -(g/L)·sin(θ) — Euler integration per frame.
//
// Tuning target: period ≈ 2.5 s at 60 fps → ω₀ = 2π/150 → g/L ≈ 0.00175
// Mouse input uses a low-pass filter so spikes never cause sudden jerks.
const GRAVITY_OVER_L = 0.0018 // swing period ~2.5 s
const LANTERN_DAMP = 0.91 // high damping — settles in ~0.5 s when mouse stops
const LANTERN_MOUSE_SCALE = 0.00028

const tickPendulum = () => {
  smoothMouseVelX *= 0.88

  const alpha = -GRAVITY_OVER_L * Math.sin(pendulumAngle.value)
  // CSS rotate(+θ) with pivot at top moves the body LEFT (Y-axis down inverts intuition).
  // Moving right (+vel) → body should lag LEFT → need positive angle → add force.
  pendulumOmega += alpha + smoothMouseVelX * LANTERN_MOUSE_SCALE
  pendulumOmega *= LANTERN_DAMP
  pendulumAngle.value += pendulumOmega

  // Clamp ±40° — subtle tilt only
  const MAX = Math.PI / 4.5
  if (Math.abs(pendulumAngle.value) > MAX) {
    pendulumAngle.value = Math.sign(pendulumAngle.value) * MAX
    pendulumOmega *= -0.1
  }

  const settled =
    !isCreativeHovered.value &&
    Math.abs(pendulumAngle.value) < 0.0006 &&
    Math.abs(pendulumOmega) < 0.0006

  if (settled) {
    pendulumAngle.value = 0
    pendulumOmega = 0
    pendulumRafId = null
    return
  }

  pendulumRafId = requestAnimationFrame(tickPendulum)
}

const startPendulum = () => {
  if (!pendulumRafId) pendulumRafId = requestAnimationFrame(tickPendulum)
}

const stopPendulum = () => {
  if (pendulumRafId) {
    cancelAnimationFrame(pendulumRafId)
    pendulumRafId = null
  }
  pendulumAngle.value = 0
  pendulumOmega = 0
}

// ─── Flame flicker (JS-driven, truly random) ──────────────────────────────────
// Three behavioral modes weighted by probability mirror real flame physics:
//   60% micro-trembles → fast, subtle (turbulent core)
//   32% breaths        → slower, wider swing (convection cycle)
//    8% gusts          → rapid, dramatic dip (sudden air movement)
let flameActive = false

const flickerFlame = () => {
  if (!flameActive || !lanternGlowRef.value) return

  const r = Math.random()
  let duration, sy, op, bl

  if (r < 0.65) {
    // Micro-tremble
    duration = 0.1 + Math.random() * 0.15
    sy = 0.96 + Math.random() * 0.08
    op = 0.88 + Math.random() * 0.12
    bl = 15 + Math.random() * 3
  } else if (r < 0.95) {
    // Breath
    duration = 0.22 + Math.random() * 0.28
    sy = 0.92 + Math.random() * 0.14
    op = 0.8 + Math.random() * 0.2
    bl = 13 + Math.random() * 6
  } else {
    // Gust — subtle dip
    duration = 0.1 + Math.random() * 0.1
    sy = 0.84 + Math.random() * 0.1
    op = 0.72 + Math.random() * 0.16
    bl = 12 + Math.random() * 5
  }

  gsap.to(lanternGlowRef.value, {
    scaleX: 0.96 + Math.random() * 0.08,
    scaleY: sy,
    opacity: op,
    filter: `blur(${bl}px)`,
    duration,
    ease: 'sine.inOut',
    overwrite: 'auto',
    onComplete: flickerFlame,
  })
}

const startFlame = () => {
  if (flameActive) return
  flameActive = true
  flickerFlame()
}

const stopFlame = () => {
  flameActive = false
  if (lanternGlowRef.value) {
    gsap.to(lanternGlowRef.value, {
      scaleX: 1,
      scaleY: 1,
      opacity: 1,
      filter: 'blur(16px)',
      duration: 0.5,
      ease: 'power2.out',
    })
  }
}

// ─── Bloom pulse (flower "breathing", light mode) ──────────────────────────
// Three behavioral modes mirroring the flame but tuned for a gentler, floral feel:
//   55% soft pulse → gentle petal sway
//   30% slow breath → deeper, slower expansion
//   15% quick flutter → rapid, subtle tremor
const bloomFlower = () => {
  if (!bloomActive || !flowerGlowRef.value) return

  const r = Math.random()
  let duration, sy, op, bl

  if (r < 0.55) {
    duration = 0.12 + Math.random() * 0.16
    sy = 0.97 + Math.random() * 0.06
    op = 0.9 + Math.random() * 0.1
    bl = 8 + Math.random() * 4
  } else if (r < 0.85) {
    duration = 0.25 + Math.random() * 0.3
    sy = 0.93 + Math.random() * 0.12
    op = 0.82 + Math.random() * 0.18
    bl = 7 + Math.random() * 5
  } else {
    duration = 0.08 + Math.random() * 0.1
    sy = 1.02 + Math.random() * 0.05
    op = 0.88 + Math.random() * 0.12
    bl = 6 + Math.random() * 4
  }

  gsap.to(flowerGlowRef.value, {
    scaleX: 0.96 + Math.random() * 0.08,
    scaleY: sy,
    opacity: op,
    filter: `blur(${bl}px)`,
    duration,
    ease: 'sine.inOut',
    overwrite: 'auto',
    onComplete: bloomFlower,
  })
}

const startBloom = () => {
  if (bloomActive) return
  bloomActive = true
  bloomFlower()
}

const stopBloom = () => {
  bloomActive = false
  if (flowerGlowRef.value) {
    gsap.to(flowerGlowRef.value, {
      scaleX: 1,
      scaleY: 1,
      opacity: 1,
      filter: 'blur(8px)',
      duration: 0.5,
      ease: 'power2.out',
    })
  }
}

// ─── Per-service animations ────────────────────────────────────────────────────

const animateInteractionIn = (index) => {
  const el = serviceItemRefs.value[index]
  if (!el || prefersReducedMotion()) return
  const title = el.querySelector('.service-item__title')
  const rippleContainer = el.querySelector('.interaction-ripples')

  // Lift with springy overshoot
  gsap.to(title, { y: -14, scale: 1.03, duration: 0.55, ease: 'back.out(2.2)' })

  // Spawn ripple rings expanding from the element
  if (rippleContainer) {
    for (let i = 0; i < 3; i++) {
      const ring = document.createElement('span')
      ring.className = 'interaction-ring'
      rippleContainer.appendChild(ring)
      gsap.fromTo(
        ring,
        { opacity: 0.6 - i * 0.15, scale: 0 },
        {
          opacity: 0,
          scale: 2.6,
          duration: 0.8 + i * 0.12,
          delay: i * 0.14,
          ease: 'power2.out',
          onComplete: () => ring.remove(),
        }
      )
    }
  }

  sectionRef.value?.classList.add('interaction-active')
}

const animateInteractionOut = (index) => {
  const el = serviceItemRefs.value[index]
  if (el) {
    const title = el.querySelector('.service-item__title')
    // Elastic snap-back — the "spring" settles
    gsap.to(title, {
      y: 0,
      scale: 1,
      rotationX: 0,
      rotationY: 0,
      duration: 0.75,
      ease: 'elastic.out(1, 0.5)',
    })
  }
  sectionRef.value?.classList.remove('interaction-active')
}

const animateProductIn = (index) => {
  const el = serviceItemRefs.value[index]
  if (!el || prefersReducedMotion()) return
  const fill = el.querySelector('.product-progress__fill')
  const milestones = el.querySelectorAll('.product-milestone')
  const badge = el.querySelector('.product-badge')
  if (fill) gsap.to(fill, { width: '100%', duration: 0.8, ease: 'power2.out' })
  if (milestones.length) {
    gsap.to(milestones, {
      opacity: 1,
      scale: 1,
      duration: 0.3,
      ease: 'back.out(1.7)',
      stagger: 0.15,
      delay: 0.3,
    })
  }
  if (badge) gsap.to(badge, { opacity: 1, y: 0, duration: 0.3, delay: 0.6, ease: 'power2.out' })
}

const animateProductOut = (index) => {
  const el = serviceItemRefs.value[index]
  if (!el) return
  const fill = el.querySelector('.product-progress__fill')
  const milestones = el.querySelectorAll('.product-milestone')
  const badge = el.querySelector('.product-badge')
  if (fill) gsap.to(fill, { width: '25%', duration: 0.5, ease: 'power2.in' })
  if (milestones.length) gsap.to(milestones, { opacity: 0, scale: 0, duration: 0.2 })
  if (badge) gsap.to(badge, { opacity: 0, y: 5, duration: 0.2 })
}

// ─── Tooltip text animation ────────────────────────────────────────────────────

const animateTextChange = (newIndex) => {
  if (!tooltipTextRef.value || currentServiceIndex.value === newIndex) return
  gsap.to(tooltipTextRef.value, {
    y: -15,
    opacity: 0,
    duration: 0.15,
    ease: 'power2.in',
    onComplete: () => {
      currentServiceIndex.value = newIndex
      nextTick(() => {
        gsap.fromTo(
          tooltipTextRef.value,
          { y: 15, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.2, ease: 'power2.out' }
        )
      })
    },
  })
}

// ─── Cursor rotation toward nearest service ────────────────────────────────────
// The SVG tip points ~-135° (upper-left) at 0°rotation, so we offset by +135° to align it.

const updateCursorRotation = (mouseX, mouseY) => {
  if (!isDesktop.value) return

  if (hoveredServices.value.size > 0) {
    cursorRotation.value += (0 - cursorRotation.value) * 0.18
    return
  }

  let minDist = Infinity
  let nearestAngleDeg = 0
  serviceItemRefs.value.forEach((el) => {
    if (!el) return
    const rect = el.getBoundingClientRect()
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    const dist = Math.hypot(cx - mouseX, cy - mouseY)
    if (dist < minDist) {
      minDist = dist
      nearestAngleDeg = Math.atan2(cy - mouseY, cx - mouseX) * (180 / Math.PI)
    }
  })
  const target = nearestAngleDeg + 135
  // Accumulate delta to always take the shortest arc (avoids 360° wrap-around spin)
  const delta = ((((target - cursorRotation.value) % 360) + 540) % 360) - 180
  cursorRotation.value += delta
}

// ─── Cursor init when section scrolls into view ────────────────────────────────
// Called by ScrollTrigger onEnter/onEnterBack — covers the case where the mouse
// is already stationary inside the section when it enters the viewport by scroll.
const initCursorIfOverSection = () => {
  if (!isDesktop.value || !sectionRef.value || !cursorRef.value) return
  const rect = sectionRef.value.getBoundingClientRect()
  const inside =
    lastMouseX >= rect.left &&
    lastMouseX <= rect.right &&
    lastMouseY >= rect.top &&
    lastMouseY <= rect.bottom
  if (!inside) return

  cursorX.value = lastMouseX
  cursorY.value = lastMouseY
  gsap.set(cursorRef.value, { x: lastMouseX, y: lastMouseY })
  if (!isCursorVisible.value) {
    isCursorVisible.value = true
    gsap.to(cursorRef.value, { scale: 1, duration: 0.4, ease: 'back.out(1.7)' })
  }
  updateCursorRotation(lastMouseX, lastMouseY)
}

// ─── Mouse handlers ────────────────────────────────────────────────────────────

const handleSectionMouseEnter = (event) => {
  if (!isDesktop.value) return
  if (!sectionRef.value || !cursorRef.value) return

  cursorX.value = event.clientX
  cursorY.value = event.clientY

  gsap.set(cursorRef.value, { x: cursorX.value, y: cursorY.value })

  if (!isCursorVisible.value) {
    isCursorVisible.value = true
    gsap.to(cursorRef.value, { scale: 1, duration: 0.4, ease: 'back.out(1.7)' })
  }

  updateCursorRotation(event.clientX, event.clientY)
}

const handleMouseEnter = (index, event) => {
  if (!isDesktop.value) return // mobile uses handleServiceClick, not hover events

  if (!hasInteracted.value) {
    hasInteracted.value = true
  }

  hoveredServices.value.add(index)
  tooltipX.value = event.clientX + 20
  tooltipY.value = event.clientY + 10
  cursorX.value = event.clientX
  cursorY.value = event.clientY
  if (isDesktop.value) {
    isCursorVisible.value = true
    gsap.to(cursorRef.value, { scale: 0.55, duration: 0.4, ease: 'back.out(1.7)' })
  }

  if (currentServiceIndex.value === null) {
    currentServiceIndex.value = index
  } else {
    animateTextChange(index)
  }

  showTiltIndicator.value = !!(services[index].hasCreativeEffect && !isDesktop.value)

  const item = serviceItemRefs.value[index]
  if (item) {
    gsap.to(item.querySelector('.service-item__title'), {
      scale: 1.05,
      duration: 0.4,
      ease: 'power4.out',
    })
  }

  // Cursor state
  if (services[index].hasFrame && isDesktop.value) {
    cursorState.value = 'pointer'
  } else if (services[index].hasCreativeEffect) {
    cursorState.value = 'hidden'
  } else {
    cursorState.value = 'default'
  }

  // INTERACTION: letter glitch + ripple rings + section class
  if (services[index].hasInteractionEffect) {
    animateInteractionIn(index)
  }
}

const handleMouseLeave = (index) => {
  if (!isDesktop.value) return // mobile uses handleServiceClick, not hover events

  hoveredServices.value.delete(index)

  const item = serviceItemRefs.value[index]
  if (item) {
    gsap.to(item.querySelector('.service-item__title'), {
      scale: 1,
      duration: 0.3,
      ease: 'power2.out',
    })
  }

  cursorState.value = 'default'

  if (hoveredServices.value.size === 0) {
    gsap.to(cursorRef.value, { scale: 1, duration: 0.3, ease: 'power2.out' })
    setTimeout(() => {
      if (hoveredServices.value.size === 0) currentServiceIndex.value = null
    }, 100)
  }

  if (services[index].hasInteractionEffect) animateInteractionOut(index)
}

const handleServiceMouseMove = (event, index) => {
  tooltipX.value = event.clientX + 20
  tooltipY.value = event.clientY + 10
  if (services[index].hasLightEffect) {
    const rect = event.currentTarget.getBoundingClientRect()
    humanityLightX.value = event.clientX - rect.left
    humanityLightY.value = event.clientY - rect.top
  }
  // INTERACTION: 3D perspective tilt follows the cursor within the element
  if (services[index].hasInteractionEffect && isDesktop.value) {
    const rect = event.currentTarget.getBoundingClientRect()
    const mx = (event.clientX - rect.left) / rect.width // 0–1
    const my = (event.clientY - rect.top) / rect.height // 0–1
    const rotY = (mx - 0.5) * 26 // ±13 deg, left ↔ right
    const rotX = (my - 0.5) * -18 // ±9 deg, up ↕ down (inverted: top → forward tilt)
    const el = serviceItemRefs.value[index]
    const title = el?.querySelector('.service-item__title')
    if (title) {
      gsap.to(title, {
        rotationX: rotX,
        rotationY: rotY,
        transformPerspective: 700,
        duration: 0.2,
        ease: 'power2.out',
        overwrite: 'auto',
      })
    }
  }
}

const handleSectionMouseLeave = () => {
  isCursorVisible.value = false
  gsap.to(cursorRef.value, { scale: 0, duration: 0.3, ease: 'power2.in' })
}

// Mobile: close the modal when the user taps on any area that isn't a service item.
// Service item clicks bubble up here too, but we skip them via the contains() check.
const handleSectionClick = (e) => {
  if (isDesktop.value || !isMobileModalOpen.value) return
  const hitServiceItem = serviceItemRefs.value.some((el) => el && el.contains(e.target))
  if (!hitServiceItem) closeMobileModal()
}

const handleSectionMouseMove = (event) => {
  // On mobile, touch events synthesize mousemove with finger coordinates — skip entirely.
  // Letting it run would move the lantern/flower to whatever the user tapped last.
  if (!isDesktop.value) return
  if (!sectionRef.value || !cursorRef.value) return

  // Low-pass filter on mouse velocity → smooth physics input, no jerk spikes
  const now = performance.now()
  const dt = now - prevMouseTimeForPendulum
  if (dt > 0 && dt < 80) {
    const rawVel = ((event.clientX - prevMouseXForPendulum) / dt) * 16
    smoothMouseVelX = smoothMouseVelX * 0.72 + rawVel * 0.28
  }
  prevMouseXForPendulum = event.clientX
  prevMouseTimeForPendulum = now

  cursorX.value = event.clientX
  cursorY.value = event.clientY
  gsap.to(cursorRef.value, {
    x: cursorX.value,
    y: cursorY.value,
    duration: 0.1,
    ease: 'power2.out',
  })

  if (isDesktop.value) {
    updateCursorRotation(event.clientX, event.clientY)
    if (!isCursorVisible.value) {
      isCursorVisible.value = true
      gsap.to(cursorRef.value, { scale: 1, duration: 0.4, ease: 'back.out(1.7)' })
    }
  }
}

// ─── Mobile handlers ───────────────────────────────────────────────────────────

const handleServiceClick = (index) => {
  if (!isDesktop.value) {
    if (currentServiceIndex.value === index && isMobileModalOpen.value) {
      closeMobileModal()
      return
    }
    // If switching away from INTERACTION, snap its tilt back to flat first
    const interactionIndex = services.findIndex((s) => s.hasInteractionEffect)
    if (
      isMobileModalOpen.value &&
      currentServiceIndex.value === interactionIndex &&
      index !== interactionIndex
    ) {
      animateInteractionOut(interactionIndex)
    }

    // Pre-position lantern/flower BEFORE the modal becomes visible so it
    // appears at the right spot immediately — no delayed jump.
    // After the auto-scroll the element will sit at MOBILE_SCROLL_OFFSET_FACTOR
    // from the top of the viewport, so we can predict the final center now.
    const creativeIndex = services.findIndex((s) => s.hasCreativeEffect)
    if (index === creativeIndex) {
      const el = serviceItemRefs.value[index]
      if (el) {
        const rect = el.getBoundingClientRect()
        const offset = window.innerHeight * MOBILE_SCROLL_OFFSET_FACTOR
        cursorX.value = rect.left + rect.width / 2
        cursorY.value = offset + rect.height / 2
      }
    }

    currentServiceIndex.value = index
    isMobileModalOpen.value = true
    isCursorVisible.value = false
    hasInteracted.value = true
    isAutoScrolling.value = true

    // INTERACTION: trigger lift + ripple animation and recalibrate gyroscope
    // so whatever angle the phone is held at right now becomes the neutral (0,0) position
    if (services[index].hasInteractionEffect) {
      animateInteractionIn(index)
      isOrientationCalibrated = false
    }

    nextTick(() => {
      const item = serviceItemRefs.value[index]
      if (item) {
        const rect = item.getBoundingClientRect()
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const offset = window.innerHeight * MOBILE_SCROLL_OFFSET_FACTOR
        window.scrollTo({ top: rect.top + scrollTop - offset, behavior: 'smooth' })
        autoScrollTimer = setTimeout(() => {
          isAutoScrolling.value = false
        }, 800)
      } else {
        isAutoScrolling.value = false
      }
    })
  }
}

const closeMobileModal = () => {
  // Reset INTERACTION tilt back to flat before closing
  const interactionIndex = services.findIndex((s) => s.hasInteractionEffect)
  if (currentServiceIndex.value === interactionIndex) {
    animateInteractionOut(interactionIndex)
  }
  isMobileModalOpen.value = false
}

const onScrollCloseModal = () => {
  if (isMobileModalOpen.value && !isAutoScrolling.value) closeMobileModal()
}

const onScrollDesktop = () => {
  if (!isDesktop.value || currentServiceIndex.value === null) return
  const item = serviceItemRefs.value[currentServiceIndex.value]
  if (!item) return
  const rect = item.getBoundingClientRect()
  const distTop = rect.top - cursorY.value
  const distBottom = cursorY.value - rect.bottom
  if (distTop > SCROLL_DEACTIVATION_THRESHOLD || distBottom > SCROLL_DEACTIVATION_THRESHOLD) {
    handleMouseLeave(currentServiceIndex.value)
  }
}

const resetSectionState = () => {
  hoveredServices.value.clear()
  currentServiceIndex.value = null
  isCursorVisible.value = false
  cursorState.value = 'default'
  stopKiLoop()
  stopPendulum()
  stopFlame()
  stopBloom()
  serviceItemRefs.value.forEach((item) => {
    if (item) {
      gsap.to(item.querySelector('.service-item__title'), {
        scale: 1,
        y: 0,
        rotationX: 0,
        rotationY: 0,
        duration: 0.4,
        ease: 'power2.out',
      })
    }
  })
  sectionRef.value?.classList.remove('interaction-active')
}

watch(isMobileModalOpen, (val) => {
  if (val) {
    window.addEventListener('scroll', onScrollCloseModal, { passive: true })
  } else {
    window.removeEventListener('scroll', onScrollCloseModal)
  }
})

watch(isCreativeHovered, (active) => {
  if (active) {
    startPendulum()
    if (isDarkMode.value) {
      startFlame()
    } else {
      startBloom()
    }
  } else {
    stopFlame()
    stopBloom()
  }
})

watch(isDarkMode, (dark) => {
  if (!isCreativeHovered.value) return
  if (dark) {
    stopBloom()
    startFlame()
  } else {
    stopFlame()
    startBloom()
  }
})

// ─── Device orientation (BiomaInsects + INTERACTION parallax) ─────────────────

let initialOrientation = { beta: 0, gamma: 0 }
let isOrientationCalibrated = false

const handleDeviceOrientation = (e) => {
  if (!sectionRef.value) return
  if (!isOrientationCalibrated) {
    initialOrientation = { beta: e.beta || 0, gamma: e.gamma || 0 }
    isOrientationCalibrated = true
    return
  }
  const gamma = (e.gamma || 0) - initialOrientation.gamma
  const beta = (e.beta || 0) - initialOrientation.beta

  // ── BiomaInsects light/cursor tracking ────────────────────────────────────
  if (biomaRef.value) {
    const { innerWidth, innerHeight } = window
    const sensitivity = 15
    biomaRef.value.setInteractionPoint(
      innerWidth / 2 + gamma * sensitivity,
      innerHeight / 2 + beta * sensitivity
    )
  }

  // ── INTERACTION: 3-D parallax tilt while item is active on mobile ─────────
  const interactionIndex = services.findIndex((s) => s.hasInteractionEffect)
  if (
    !isDesktop.value &&
    isMobileModalOpen.value &&
    currentServiceIndex.value === interactionIndex
  ) {
    // Clamp to ±45° of tilt, map to the same rotation range as the desktop hover
    const clampedGamma = Math.max(-45, Math.min(45, gamma))
    const clampedBeta = Math.max(-45, Math.min(45, beta))
    const rotY = (clampedGamma / 45) * 13 // ±13° left↔right (matches mouse rotY)
    const rotX = -(clampedBeta / 45) * 9 // ±9°  forward↕back (matches mouse rotX)

    const el = serviceItemRefs.value[interactionIndex]
    const title = el?.querySelector('.service-item__title')
    if (title) {
      gsap.to(title, {
        rotationX: rotX,
        rotationY: rotY,
        transformPerspective: 700,
        duration: 0.12,
        ease: 'power2.out',
        overwrite: 'auto',
      })
    }
  }
}

// ─── Lifecycle ─────────────────────────────────────────────────────────────────

onMounted(() => {
  setTimeout(() => {
    if (!sectionRef.value) return

    scrollTriggerInstance = ScrollTrigger.create({
      trigger: sectionRef.value,
      start: 'top 50%',
      end: 'top 10%',
      scrub: 0.8,
      onUpdate: (self) => {
        const totalProgress = self.progress * 100
        const progressPerItem = 100 / services.length
        services.forEach((service, index) => {
          const itemStart = index * progressPerItem
          const itemEnd = (index + 1) * progressPerItem
          if (totalProgress <= itemStart) service.progress = 0
          else if (totalProgress >= itemEnd) service.progress = 100
          else service.progress = ((totalProgress - itemStart) / progressPerItem) * 100
        })
      },
    })

    ScrollTrigger.create({
      trigger: sectionRef.value,
      start: 'top bottom',
      end: 'bottom top',
      onEnter: initCursorIfOverSection,
      onEnterBack: initCursorIfOverSection,
      onLeave: resetSectionState,
      onLeaveBack: resetSectionState,
    })

    if (headerRef.value) {
      gsap.from(headerRef.value.children, {
        y: 30,
        opacity: 0,
        duration: 0.8,
        stagger: 0.15,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: headerRef.value,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      })
    }
  }, 100)

  if (window.DeviceOrientationEvent) {
    window.addEventListener('deviceorientation', handleDeviceOrientation)
  }
  window.addEventListener('scroll', onScrollDesktop, { passive: true })
  window.addEventListener('mousemove', trackGlobalMouse, { passive: true })

  // Track theme changes so isDarkMode stays reactive
  themeObserver = new MutationObserver(() => {
    isDarkMode.value = document.documentElement.classList.contains('dark')
  })
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  })
})

onUnmounted(() => {
  if (scrollTriggerInstance) scrollTriggerInstance.kill()
  if (autoScrollTimer) clearTimeout(autoScrollTimer)
  if (pendulumRafId) cancelAnimationFrame(pendulumRafId)
  if (themeObserver) themeObserver.disconnect()
  stopFlame()
  stopBloom()
  window.removeEventListener('deviceorientation', handleDeviceOrientation)
  window.removeEventListener('scroll', onScrollDesktop)
  window.removeEventListener('mousemove', trackGlobalMouse)
})
</script>

<style scoped>
/* ─── Section ──────────────────────────────────────────────────────────────── */
.what-i-do {
  @apply relative;
  min-height: 100svh;
  background: var(--color-deep-black);
  overflow: hidden;
}

@media (hover: hover) {
  .what-i-do {
    cursor: none;
  }
}

.what-i-do__inner {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100svh;
  padding: 5rem 0;
  position: relative;
}

.bioma-insects-overlay {
  @apply fixed inset-0 pointer-events-none;
  z-index: 20;
  opacity: 0;
  transition: opacity 0.5s ease;
}

.bioma-insects-overlay--visible {
  opacity: 1;
}

/* ─── Container + Header ───────────────────────────────────────────────────── */
.what-i-do__container {
  @apply w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-16;
  position: relative;
  z-index: 25;
}

.what-i-do__header {
  @apply mb-16  text-center;
}

.what-i-do__title {
  @apply font-madi text-4xl sm:text-5xl md:text-7xl font-bold lowercase;
  color: var(--color-text-primary);
}

.what-i-do__title-accent {
  @apply pr-2;
  color: var(--color-electric-purple);
}

/* ─── Services layout ──────────────────────────────────────────────────────── */
.what-i-do__services {
  @apply flex flex-wrap items-center justify-center gap-x-6 gap-y-8 md:gap-x-10 lg:gap-x-14;
}

@media (max-width: 768px) {
  .what-i-do__services {
    @apply gap-x-4 gap-y-10;
  }

  .what-i-do__title {
    @apply text-3xl sm:text-4xl;
  }
}

/* ─── Service item ─────────────────────────────────────────────────────────── */
.service-item {
  @apply relative;
  transition: opacity 0.3s ease;
}

.service-item.is-hovered {
  cursor: none;
}

.service-item--dimmed {
  opacity: 0.3;
  transition: opacity 0.3s ease;
}

/* interaction-active: slow global transitions while INTERACTION is hovered */
:global(.interaction-active) .service-item {
  transition: all 0.7s ease !important;
}

/* ─── Title ────────────────────────────────────────────────────────────────── */
.service-item__title {
  @apply relative;
  /* fluid: ~2.25rem on mobile → ~8rem on ultrawide, smooth all the way */
  font-size: clamp(2.25rem, 7vw, 8rem);
  @apply font-black tracking-tighter leading-none;
  font-family: 'Plus Jakarta Sans', sans-serif;
  white-space: nowrap;
  will-change: transform;
  z-index: 1;
}

/* HUMANITY: always one step above — ~3rem mobile → ~10rem ultrawide */
.service-item--humanity .service-item__title {
  font-size: clamp(3rem, 9vw, 10rem);
}

.service-item__text {
  @apply block select-none;
}

.service-item__text--bg {
  color: var(--color-charcoal);
  opacity: 0.35;
  transition: opacity 0.4s ease;
}

:root.dark .service-item__text--bg {
  color: #252525;
  opacity: 0.6;
}

.service-item__text--fill {
  @apply absolute inset-0;
  background: linear-gradient(90deg, var(--color-ash) 0%, var(--color-text-primary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  transition:
    clip-path 0.08s ease-out,
    opacity 0.4s ease;
  padding-right: 0.05em;
  margin-right: -0.05em;
  margin-bottom: -5px;
}

:root.dark .service-item__text--fill {
  background: linear-gradient(90deg, #a3a3a3 0%, #f5f5f5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
@media (hover: hover) {
  .service-item:hover .service-item__text--fill {
    background: linear-gradient(90deg, var(--color-electric-purple) 0%, #a78bfa 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
}

.service-item--active .service-item__text--fill {
  background: linear-gradient(90deg, var(--color-electric-purple) 0%, #a78bfa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
/* ─── UI/UX: Figma frame ───────────────────────────────────────────────────── */
.service-item__frame {
  @apply absolute inset-0 pointer-events-none;
  border: 2px solid #0d99ff;
  opacity: 0;
  transform: scale(0.98);
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  margin: -11px -25px;
  margin-top: 0;
  padding: 8px 12px;
}

@media (hover: hover) {
  .service-item--has-frame:hover .service-item__frame {
    opacity: 1;
    transform: scale(1.05);
  }
}

.service-item--active.service-item--has-frame .service-item__frame {
  opacity: 1;
  transform: scale(1.05);
}

.service-item__frame-corner {
  @apply absolute;
  width: 8px;
  height: 8px;
  background: #fff;
  border-radius: 1px;
}

.service-item__frame-corner::after {
  content: '';
  @apply absolute;
  width: 100%;
  height: 100%;
  border: 2px solid #0d99ff;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 1px;
}

.service-item__frame-corner--tl {
  top: -4px;
  left: -4px;
}
.service-item__frame-corner--tr {
  top: -4px;
  right: -4px;
}
.service-item__frame-corner--bl {
  bottom: -4px;
  left: -4px;
}
.service-item__frame-corner--br {
  bottom: -4px;
  right: -4px;
}

.service-item__frame-size {
  @apply absolute text-xs font-medium;
  bottom: -24px;
  left: 50%;
  transform: translateX(-50%);
  background: #0d99ff;
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
}

/* ─── FRONTEND: code tags ──────────────────────────────────────────────────── */
.tag {
  @apply absolute text-4xl md:text-8xl font-mono font-bold;
  color: var(--color-electric-purple);
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  pointer-events: none;
}

.tag--open {
  @apply -left-6 lg:-left-16;
}
.tag--close {
  @apply -right-[2.9rem] lg:-right-28;
}

@media (hover: hover) {
  .service-item:hover .tag--open {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
    animation: none;
  }
  .service-item:hover .tag--close {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
    animation: none;
  }
}

.service-item--active .tag--open,
.service-item--active .tag--close {
  opacity: 1;
  animation: none;
}

/* ─── INTERACTION ───────────────────────────────────────────────────────────── */

.interaction-effect {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: visible;
}

/* Ripple ring container */
.interaction-ripples {
  position: absolute;
  inset: -10px -14px;
  pointer-events: none;
  overflow: visible;
}

/* Individual ring — spawned and removed dynamically by GSAP */
.interaction-ring {
  position: absolute;
  inset: 0;
  border-radius: 8px;
  border: 2px solid var(--color-electric-purple);
  opacity: 0;
  pointer-events: none;
  transform-origin: center;
}

/* ─── HUMANITY: moving light effect (in text) ──────────────────────────────── */
@media (hover: hover) {
  .service-item.service-item--humanity:hover .service-item__text--fill {
    background:
      radial-gradient(
        circle 180px at var(--light-x, 50%) var(--light-y, 50%),
        rgba(255, 255, 255, 0.9) 0%,
        rgba(196, 181, 253, 0.6) 30%,
        rgba(139, 92, 246, 0.25) 60%,
        transparent 80%
      ),
      linear-gradient(90deg, var(--color-ash) 0%, var(--color-text-primary) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
}

.service-item.service-item--humanity.service-item--active .service-item__text--fill {
  background:
    radial-gradient(
      circle 180px at var(--light-x, 50%) var(--light-y, 50%),
      rgba(255, 255, 255, 0.9) 0%,
      rgba(196, 181, 253, 0.6) 30%,
      rgba(139, 92, 246, 0.25) 60%,
      transparent 80%
    ),
    linear-gradient(90deg, var(--color-ash) 0%, var(--color-text-primary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

:root.dark .service-item.service-item--humanity:hover .service-item__text--fill,
:root.dark .service-item.service-item--humanity.service-item--active .service-item__text--fill {
  background:
    radial-gradient(
      circle 180px at var(--light-x, 50%) var(--light-y, 50%),
      rgba(255, 255, 255, 0.9) 0%,
      rgba(196, 181, 253, 0.6) 30%,
      rgba(139, 92, 246, 0.25) 60%,
      transparent 80%
    ),
    linear-gradient(90deg, #a3a3a3 0%, #f5f5f5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ─── HUMANITY: text-based outer glow ──────────────────────────────────────── */
.service-item__text--glow {
  @apply absolute inset-0 pointer-events-none;
  padding: 0 0.15em;
  margin: 0 -0.15em;
  margin-bottom: -5px;
  background: radial-gradient(
    circle 220px at var(--light-x, 50%) var(--light-y, 50%),
    rgba(255, 255, 255, 1) 0%,
    transparent 80%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.9))
    drop-shadow(0 0 25px rgba(196, 181, 253, 0.8)) drop-shadow(0 0 45px rgba(139, 92, 246, 0.6));

  opacity: 0;
  transition: opacity 0.4s ease;
  z-index: -1;
}

@media (hover: hover) {
  .service-item.service-item--humanity:hover .service-item__text--glow {
    opacity: 1;
  }
}

.service-item.service-item--humanity.service-item--active .service-item__text--glow {
  opacity: 1;
}

/* ─── GENKIDAMA: dynamic bright border/stroke ───────────────────────────────── */
.service-item__text--stroke {
  @apply absolute inset-0 pointer-events-none;
  padding: 0 0.15em;
  margin: 0 -0.15em;
  margin-bottom: -5px;
  /* Use black text with screen blending to hide the interior and only show the shadow */
  color: #000;
  mix-blend-mode: screen;

  text-shadow:
    -1.5px -1.5px 0 rgba(255, 255, 255, 0.95),
    1.5px -1.5px 0 rgba(255, 255, 255, 0.95),
    -1.5px 1.5px 0 rgba(255, 255, 255, 0.95),
    1.5px 1.5px 0 rgba(255, 255, 255, 0.95),
    0 -1.5px 0 rgba(255, 255, 255, 0.95),
    0 1.5px 0 rgba(255, 255, 255, 0.95),
    -1.5px 0 0 rgba(255, 255, 255, 0.95),
    1.5px 0 0 rgba(255, 255, 255, 0.95),
    0 0 12px rgba(196, 181, 253, 0.8);

  -webkit-mask-image: radial-gradient(
    circle 130px at var(--light-x, 50%) var(--light-y, 50%),
    black 0%,
    rgba(0, 0, 0, 0.4) 40%,
    transparent 80%
  );
  mask-image: radial-gradient(
    circle 130px at var(--light-x, 50%) var(--light-y, 50%),
    black 0%,
    rgba(0, 0, 0, 0.4) 40%,
    transparent 80%
  );

  opacity: 0;
  transition: opacity 0.4s ease;
  z-index: 2; /* Ensure it stays above the fill */
}

@media (hover: hover) {
  .service-item.service-item--humanity:hover .service-item__text--stroke {
    opacity: 1;
  }
}

.service-item.service-item--humanity.service-item--active .service-item__text--stroke {
  opacity: 1;
}

.ki-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 30;
}

/* ─── Tooltip ──────────────────────────────────────────────────────────────── */
.service-tooltip {
  @apply fixed z-50 pointer-events-none;
  @apply px-4 py-3 rounded-xl;
  @apply text-sm font-medium;
  @apply flex items-center gap-3;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-electric-purple);
  color: var(--color-text-primary);
  box-shadow:
    0 10px 40px rgba(0, 0, 0, 0.2),
    0 0 20px rgba(124, 58, 237, 0.15);
  backdrop-filter: blur(12px);
  max-width: 300px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(8px) scale(0.95);
  transition:
    opacity 0.25s ease,
    transform 0.25s ease,
    visibility 0.25s ease;
}

.service-tooltip--visible {
  opacity: 1;
  visibility: visible;
  transform: translateY(0) scale(1);
}

:root.dark .service-tooltip {
  background: rgba(20, 20, 20, 0.95);
  border-color: rgba(124, 58, 237, 0.5);
  box-shadow:
    0 10px 40px rgba(0, 0, 0, 0.5),
    0 0 30px rgba(124, 58, 237, 0.2);
}

.service-tooltip__icon {
  @apply text-lg;
  color: var(--color-electric-purple);
  animation: sparkle 1.5s ease-in-out infinite;
}

@keyframes sparkle {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
}

.service-tooltip__content {
  overflow: hidden;
}

.service-tooltip__text-wrapper {
  overflow: hidden;
}

.service-tooltip__text {
  @apply block;
  line-height: 1.5;
  will-change: transform, opacity;
}

@media (max-width: 768px) {
  .service-tooltip {
    display: none;
  }
}

/* ─── Custom cursor ────────────────────────────────────────────────────────── */
.custom-cursor {
  @apply fixed pointer-events-none z-[100];
  top: 0;
  left: 0;
  width: 48px;
  height: 48px;
  opacity: 0;
  visibility: hidden;
  transform: scale(0);
  margin-top: -24px;
  margin-left: -24px;
}

.custom-cursor--visible {
  opacity: 1;
  visibility: visible;
}

.custom-cursor__icon {
  width: 100%;
  height: 100%;
  transform-origin: center center;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4));
  transition: transform 0.12s ease-out;
}

/* ─── Tap hint (positioned over first service item) ───────────────────────── */
.tap-hint {
  position: absolute;
  z-index: 50;
  pointer-events: none;
  opacity: 0;
  transform: translate(-50%, -10px);
  transition: opacity 0.5s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.tap-hint--visible {
  opacity: 1;
}

.tap-hint__icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tap-hint__svg {
  width: 28px;
  height: 28px;
  color: var(--color-electric-purple);
  animation: tap-press 1.8s ease-in-out infinite;
  position: relative;
  z-index: 2;
}

.tap-hint__ripple {
  position: absolute;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(124, 58, 237, 0.25);
  z-index: 1;
  animation: tap-ripple 1.8s ease-out infinite;
  animation-delay: 0.15s;
}

.tap-hint__label {
  @apply text-[10px] font-semibold uppercase tracking-widest;
  color: var(--color-electric-purple);
  text-align: center;
  white-space: nowrap;
  opacity: 0.8;
}

@keyframes tap-press {
  0%,
  100% {
    transform: scale(1) translateY(0);
  }
  20% {
    transform: scale(0.7) translateY(4px);
  }
  35% {
    transform: scale(1.05) translateY(-2px);
  }
  45% {
    transform: scale(1) translateY(0);
  }
}

@keyframes tap-ripple {
  0% {
    transform: scale(0.8);
    opacity: 0.7;
  }
  40% {
    transform: scale(2.2);
    opacity: 0;
  }
  100% {
    transform: scale(2.2);
    opacity: 0;
  }
}

/* ─── Service pulse (mobile, before first interaction) ────────────────────── */
@media (hover: none) and (pointer: coarse) {
  .what-i-do__services:not(.has-interacted) .service-item__text--fill {
    animation: service-pulse 3s ease-in-out infinite;
  }
}

@keyframes service-pulse {
  0%,
  100% {
    opacity: 0.45;
  }
  50% {
    opacity: 0.85;
  }
}

/* ─── Mobile modal ─────────────────────────────────────────────────────────── */
.mobile-service-modal-backdrop {
  @apply fixed inset-0 z-[100] flex items-end justify-center;
  background: transparent;
  pointer-events: none;
}

.mobile-service-modal {
  @apply w-full max-w-lg bg-[var(--color-surface-elevated)] rounded-t-2xl p-6 pb-10;
  border-top: 1px solid var(--color-border);
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.4);
  pointer-events: auto;
}

.mobile-service-modal__header {
  @apply flex justify-between items-center mb-4;
}

.mobile-service-modal__title {
  @apply text-xl font-bold text-[var(--color-text-primary)];
}

.mobile-service-modal__close {
  @apply p-2 rounded-full hover:bg-[var(--color-surface)] transition-colors;
}

.mobile-service-modal__description {
  @apply text-[var(--color-text-secondary)] leading-relaxed text-base;
}

.modal-slide-enter-active,
.modal-slide-leave-active {
  transition: opacity 0.3s ease;
}

.modal-slide-enter-active .mobile-service-modal,
.modal-slide-leave-active .mobile-service-modal {
  transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.modal-slide-enter-from,
.modal-slide-leave-to {
  opacity: 0;
}

.modal-slide-enter-from .mobile-service-modal,
.modal-slide-leave-to .mobile-service-modal {
  transform: translateY(100%);
}

/* ─── CREATIVE: lantern cursor (mouse-carried, dark mode) ───────────────────── */

/* The grip is a zero-size anchor pinned to the cursor.
   Everything hangs from here — this is the "hand". */
.lantern-grip {
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  pointer-events: none;
  z-index: 55;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.lantern-grip--visible {
  opacity: 1;
}

/* The body hangs from the grip, rotating around it (pendulum). */
.lantern-body {
  position: absolute;
  left: -30px; /* center the 60px element on the grip */
  top: -8px; /* grip overlaps slightly with the top of the lantern */
  width: 60px;
  height: 70px;
  transform-origin: 30px 8px; /* pivot at the grip point inside the element */
}

/* Ambient glow painted BEHIND the lantern image.
   Being first in the DOM it sits below the img naturally. */
.lantern-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: radial-gradient(
    circle at center,
    rgba(255, 240, 180, 0.75) 0%,
    rgba(255, 190, 80, 0.45) 30%,
    rgba(255, 140, 30, 0.18) 58%,
    rgba(220, 90, 10, 0.05) 78%,
    transparent 100%
  );
  filter: blur(16px);
  pointer-events: none;
  /* Flicker is driven by JS (flickerFlame) — no CSS animation here */
}

/* Pixel-art lantern — sits on top of the glow (DOM order) */
.lantern-img {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 58px;
  height: auto;
  image-rendering: pixelated;
  /* Only a subtle shadow for depth, the real light is .lantern-glow */
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.55));
}

/* ─── CREATIVE: flower cursor (mouse-carried, light mode) ────────────────────── */

/* The grip is a zero-size anchor pinned to the cursor (same pattern as lantern). */
.flower-grip {
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  pointer-events: none;
  z-index: 55;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.flower-grip--visible {
  opacity: 1;
}

/* The body hangs from the grip, rotating around it (pendulum). */
.flower-body {
  position: absolute;
  left: -14px;
  top: -4px;
  width: 28px;
  height: 60px;
  transform-origin: 14px 4px; /* pivot at the grip point (top of flower head) */
}

/* Warm golden glow behind the flower — bloom is driven by JS (bloomFlower) */
.flower-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: none;
  filter: blur(8px);
  pointer-events: none;
}

/* Pixel-art flower SVG — sits on top of the glow (DOM order) */
.flower-sprite {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 56px;
  image-rendering: pixelated;
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.3));
}

/* ─── Reduced motion ───────────────────────────────────────────────────────── */
@media (prefers-reduced-motion: reduce) {
  .sys-line,
  .bezier-path,
  .motion-wave-path,
  .motion-letter,
  .sys-node,
  .service-item__frame,
  .tag,
  .humanity-light {
    animation: none !important;
    transition: opacity 0.1s ease !important;
  }
}
</style>
